'use client';

import React from 'react';
import styled from 'styled-components';
import Dropdown from '@/components/forms/controls/Dropdown';
import { Input } from '@/components/forms/controls/Input';

interface GroupTagSectionProps {
  selectedGroup: string;
  onGroupChange: (group: string) => void;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  className?: string;
}

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div``;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 16px 0;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const Tag = styled.div`
  background: linear-gradient(270deg, #D33324 0%, #FB6F2A 100%);
  color: #ffffff;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RemoveTagButton = styled.button`
  background: transparent;
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const GroupTagSection: React.FC<GroupTagSectionProps> = ({
  selectedGroup,
  onGroupChange,
  tags,
  onTagsChange,
  className,
}) => {
  const [tagInput, setTagInput] = React.useState('');

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput?.trim()) {
      e.preventDefault();
      onTagsChange([...tags, tagInput?.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (index: number) => {
    onTagsChange(tags.filter((_, i) => i !== index));
  };

  return (
    <Container className={className}>
      <Section>
        <SectionTitle>Group</SectionTitle>
        <SectionDescription>
          Your monitor will be automatically added to the chosen group
        </SectionDescription>
        <Dropdown
          options={[{ label: 'Monitors (default)', value: 'default' }]}
          value={selectedGroup}
          onChange={onGroupChange}
        />
      </Section>

      <Section>
        <SectionTitle>Add tags</SectionTitle>
        <SectionDescription>
          Tags will enable you to organise your monitors in a better way
        </SectionDescription>
        <Input
          type="text"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleAddTag}
          placeholder="Click to add tag..."
          fullWidth
        />
        {tags?.length > 0 && (
          <TagList>
            {tags.map((tag, index) => (
              <Tag key={index}>
                {tag}
                <RemoveTagButton type="button" onClick={() => handleRemoveTag(index)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </RemoveTagButton>
              </Tag>
            ))}
          </TagList>
        )}
      </Section>
    </Container>
  );
};

export default GroupTagSection;

